import {
    type FC,
    useState
} from "react";
import {
    ImageBackground,
    TouchableOpacity,
    Platform,
    View
} from "react-native";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitTheme,
    NCoreUIKitLocalize,
    NCoreUIKitSnackBar,
    Text
} from "ncore-ui-kit";
import type {
    FormWidgetProps
} from "../../type";
import {
    Camera,
    CircleAlert
} from "lucide-react-native";
import ImageCropPicker, {
    type Image as CropImage
} from "react-native-image-crop-picker";

const PhotoWidget: FC<FormWidgetProps> = ({
    field,
    value,
    onChange,
    formID
}) => {
    const {
        spaces,
        colors,
        radiuses,
        borders
    } = NCoreUIKitTheme.useContext();
    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    // We can display the value directly if it is a URL, or the local path if just uploaded.
    const [localUri,
        setLocalUri] = useState<string | null>(null);

    const handleSelectImage = () => {
        if(Platform.OS === "web") {
            const input = document.createElement("input");
            input.type = "file";
            input.accept = "image/*";
            input.onchange = (e: Event) => {
                const target = e.target as HTMLInputElement;
                const file = target.files?.[0];
                if(file) {
                    const objectUrl = URL.createObjectURL(file);
                    setLocalUri(objectUrl);
                    onChange(objectUrl);
                }
            };
            input.click();
            return;
        }

        ImageCropPicker.openPicker({
            width: 1024,
            height: 1024,
            cropping: false,
            mediaType: "photo"
        }).then((image) => {
            if(image) {
                uploadImage(image);
            }
        }).catch(err => {
            console.log(err);
        });
    };

    const uploadImage = async (image: CropImage) => {
        if (!formID) {
            NCoreUIKitSnackBar.open({
                title: localize("error"),
                subTitle: localize("anErrorOccured"),
                type: "danger",
                isShowAction: false,
                autoCloseDelay: 5000,
                icon: ({
                    color
                }) => <CircleAlert
                    color={colors.content.icon[color ? color : "default"]}
                />
            });
            return;
        }

        const path = image.path.startsWith("file://") ? image.path : `file://${image.path}`;
        setLocalUri(path);
        onChange(path);
    };

    return <View
        style={{
            marginBottom: spaces.spacingMd
        }}
    >
        <Text
            variant="labelMediumSize"
            color="high"
            style={{
                marginBottom: spaces.spacingSm
            }}
        >
            {field.title} {field.isRequired !== false && <Text
                color="danger"
            >
                *
            </Text>}
        </Text>
        <TouchableOpacity
            style={[
                stylesheet.buttonContainer,
                {
                    borderColor: colors.content.border.default,
                    borderRadius: radiuses.md,
                    borderWidth: borders.line,
                    padding: spaces.spacingMd,
                    backgroundColor: colors.content.container.subtle
                }
            ]}
            onPress={handleSelectImage}
        >
            {(localUri || value) ? <ImageBackground
                source={{
                    uri: localUri ? localUri : value
                }}
                style={[
                    stylesheet.imageBackground,
                    {
                        borderRadius: radiuses.sm
                    }
                ]}
                resizeMode="contain"
            >
                <View
                    style={stylesheet.contentContainer}
                >
                    <Camera
                        color={colors.content.icon.default}
                        size={20}
                    />
                    <Text
                        variant="bodyMediumSize"
                        color="high"
                        style={{
                            marginTop: spaces.spacingXs
                        }}
                    >
                        {localUri ? localize("choosePhoto") : localize("uploadPhoto")}
                    </Text>
                </View>
            </ImageBackground> : <View
                style={[
                    stylesheet.emptyContentContainer,
                    {
                        paddingVertical: spaces.spacingXl
                    }
                ]}
            >
                <Camera
                    size={32}
                    color={colors.content.icon.default}
                />
                <Text
                    variant="bodyMediumSize"
                    color="mid"
                    style={{
                        marginTop: spaces.spacingSm
                    }}
                >
                    {localize("uploadPhoto")}
                </Text>
            </View>}
        </TouchableOpacity>
    </View>;
};

export default PhotoWidget;
